import { Box, Button, Card, Flex, Heading, Text } from '@chakra-ui/react';
import { createDataCollectionContext, dbTimeToUserTime, useDrawer, useMobileContext } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import discountCodesService from '../../api/services/discountCodesService';
import DiscountCodeDrawer from './DiscountCodeDrawer';

const columnHelper = createColumnHelper<DiscountCode>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function DiscountCodes() {
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<DiscountCode>(),
		[],
	);
	const { t } = useTranslation();
	const { isMobile } = useMobileContext();
	const { openDrawer } = useDrawer();

	const columns = [
		columnHelper.accessor('discountCodeID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('discountPercentage', {
			header: t('general.discount_percentage'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('code', {
			header: t('general.code'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('usageLimit', {
			header: t('general.usage_limit'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('discountCodeUsages', {
			header: t('general.used_times'),
			enableSorting: false,
			cell: ({ row }) => {
				return <Text>{row.original.discountCodeUsages.length}</Text>;
			},
		}),
		columnHelper.accessor('timeStart', {
			header: t('general.time_start'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeStart) {
					return <Text>{dbTimeToUserTime(row.original.timeStart, TIMEZONE)}</Text>;
				}
				return null;
			},
		}),
		columnHelper.accessor('timeEnd', {
			header: t('general.time_end'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeEnd) {
					return <Text>{dbTimeToUserTime(row.original.timeEnd, TIMEZONE)}</Text>;
				}
				return null;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.discount_codes')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}
			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="discountCodes"
								keyAttribute="discountCodeID"
								emptyDataText={t('general.no_discount_codes_found')}
								fetchFn={(...args) => discountCodesService.getAllResources(...args)}
								queryParams={{ getRelated: 'discountCodeUsages' }}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeCreated', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'discountCodeDrawer',
										content: <DiscountCodeDrawer discountCodeID={row.original.discountCodeID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
									<Button
										id="addDiscountCode"
										onClick={() => {
											openDrawer({
												id: 'discountCodeDrawer',
												content: <DiscountCodeDrawer />,
											});
										}}
									>
										{t('general.add_discount_code')}
									</Button>
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
